import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clickupSyncAdminApi } from '@/lib/api';
import type {
  ClickupAvailableSpacesResponse,
  ClickupSyncLogsResponse,
  ClickupSyncRun,
  ClickupSyncSettings,
  ClickupSyncTestResult,
  UpdateClickupSyncSettingsData,
} from '@/types';

export const clickupSyncKeys = {
  all: ['clickup-sync-admin'] as const,
  settings: () => [...clickupSyncKeys.all, 'settings'] as const,
  availableSpaces: () => [...clickupSyncKeys.all, 'available-spaces'] as const,
  logs: (limit: number) => [...clickupSyncKeys.all, 'logs', limit] as const,
};

export function useClickupSyncSettings() {
  return useQuery({
    queryKey: clickupSyncKeys.settings(),
    queryFn: async () => {
      const response = await clickupSyncAdminApi.getSettings();
      return response.data as ClickupSyncSettings;
    },
  });
}

export function useUpdateClickupSyncSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateClickupSyncSettingsData) => {
      const response = await clickupSyncAdminApi.updateSettings(data);
      return response.data as ClickupSyncSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(clickupSyncKeys.settings(), data);
      queryClient.invalidateQueries({ queryKey: clickupSyncKeys.availableSpaces() });
    },
  });
}

export function useClickupAvailableSpaces(enabled = true) {
  return useQuery({
    queryKey: clickupSyncKeys.availableSpaces(),
    queryFn: async () => {
      const response = await clickupSyncAdminApi.getAvailableSpaces();
      return response.data as ClickupAvailableSpacesResponse;
    },
    enabled,
    staleTime: 30000,
  });
}

export function useTestClickupConnection() {
  return useMutation({
    mutationFn: async () => {
      const response = await clickupSyncAdminApi.testConnection();
      return response.data as ClickupSyncTestResult;
    },
  });
}

export function useRunClickupSyncNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await clickupSyncAdminApi.runNow();
      return response.data as { run: ClickupSyncRun };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clickupSyncKeys.settings() });
      queryClient.invalidateQueries({ queryKey: clickupSyncKeys.all });
    },
  });
}

export function useClickupSyncLogs(limit = 200) {
  return useQuery({
    queryKey: clickupSyncKeys.logs(limit),
    queryFn: async () => {
      const response = await clickupSyncAdminApi.getLogs(limit);
      return response.data as ClickupSyncLogsResponse;
    },
    refetchInterval: 10000,
  });
}

export function useClearClickupSyncLogs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await clickupSyncAdminApi.clearLogs();
      return response.data as { success: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clickupSyncKeys.all });
    },
  });
}

